import React, { useState } from "react";
import { Table, Modal, Tooltip } from "antd";
import { EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import './AdComparisionTable.css'

interface Ad {
    adId: string;
    adName: string;
    thumbnail?: string;
    adPreview: string;
    spend: number;
    reach: number;
    impressions: number;
    clicks: number;
    clids: number;
    nudges?: NudgeGroup[];
    retargeting?: Retargeting[];
    // Keep accepting the legacy spelling if it is returned by an older API version.
    retargetting?: Retargeting[];
    milestones: {
        milestoneName: string;
        milestoneNum: number;
        totalCount: number;
        uniqueCount: number;
    }[];
}

interface AdData {
    campaignId: string;
    campaignName: string;
    dateRange: string;
    ads: Ad[];
    nudges?: Nudge[];
    retargeting?: Retargeting[];
    // Keep accepting the legacy spelling if it is returned by an older API version.
    retargetting?: Retargeting[];
}

interface Nudge {
    nudgeName: string;
    leadWithoutNudge: number;
    leadWithNudge: number;
}

interface NudgeGroup {
    nudgeType: string;
    values: Nudge[];
}

interface Retargeting {
    retargetingName: string;
    leadWithoutRetargeting: number;
    leadWithRetargeting: number;
}

export default function AdComparisonTable({ data }: { data: AdData }) {
    const { ads, campaignName, dateRange } = data;
    const [previewAd, setPreviewAd] = useState<Ad | null>(null);

    // Nudge and retargeting metrics belong to an ad in the funnel response.
    // Build one row per distinct name while retaining the first-seen order.
    const nudgeNames = Array.from(new Set(
        ads.flatMap((ad) => (ad.nudges || []).flatMap((group) => group.values || []).map((nudge) => nudge.nudgeName))
    ));
    const retargetingNames = Array.from(new Set(
        ads.flatMap((ad) => (ad.retargeting || ad.retargetting || []).map((item) => item.retargetingName))
    ));

    const maxMilestones = Math.max(
        ...ads.map((a) => a.milestones?.length || 0)
    );

    // -------- ROWS --------
    const rows: any[] = [
        { key: "adName", label: "Ad", type: "ad" },
        { key: "adPreview", label: "Ad Preview", type: "preview" },
        // { key: "adName", label: "Ad Name", type: "metric" },
        { key: "spend", label: "Spend", type: "metric" },
        { key: "reach", label: "Reach", type: "metric" },
        { key: "impressions", label: "Impressions", type: "metric" },
        { key: "clicks", label: "Clicks", type: "metric" },
        { key: "clids", label: "CLIDs", type: "metric" },
        ...Array.from({ length: maxMilestones }, (_, i) => ({
            key: `m_${i}`,
            label: `Milestone at level ${i + 1}`,
            type: "milestone",
            idx: i,
        })),
        ...nudgeNames.map((name, i) => ({ key: `n_${i}`, label: `Nudge-${name}`, type: "nudge", name })),
        ...retargetingNames.map((name, i) => ({ key: `r_${i}`, label: `Retargeting-${name}`, type: "retargeting", name })),
    ];

    // -------- CELL RENDERERS --------

    const renderCell = (value: string) => (
        <div className="flex items-center justify-center h-full font-semibold">
            {value?.toLocaleString?.() ?? "—"}
        </div>
    );
    const renderPreviewCell = (ad: Ad) => (
        <div className="ad-preview-cell">
            {ad.adPreview ? (
                <button
                    className="ad-preview-trigger"
                    onClick={() => setPreviewAd(ad)}
                >
                    {ad.thumbnail ? (
                        <img
                            src={ad.thumbnail}
                            alt={ad.adName}
                            className="ad-preview-thumb"
                        />
                    ) : (
                        <EyeOutlined className="ad-preview-icon" />
                    )}
                    <span className="ad-preview-label">View Ad</span>
                </button>
            ) : (
                <span className="ad-preview-na">—</span>
            )}
        </div>
    );

    const renderMetricCell = (value: number) => (
        <div className="flex items-center justify-center h-full font-semibold">
            {value?.toLocaleString?.() ?? "—"}
        </div>
    );
    const renderMilestoneCell = (m?: any) => {
        if (!m) return <div className="text-center">—</div>;

        return (
            <div className="grid grid-cols-[1fr_1fr] h-full">
                {/* LEFT: Name */}
                <div className="flex items-center justify-center border-r border-black font-semibold px-2 p-[1rem]">
                    {m.milestoneName}
                </div>

                {/* RIGHT: Partitioned counts */}
                <div className="flex text-center items-center justify-center border-l border-black font-semibold px-2">
                    {[
                        { label: m.totalCount, value: "|" },
                        { label: m.uniqueCount, value: "(U)" },
                    ].map((item, i) => (
                        <div
                            key={i}
                            className={`text-center items-center justify-center`}
                        >
                            <span className="text-xs text-gray-600">{item.label}</span>
                            <span className="font-semibold">{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderLeadCount = (value: number, label: string) => (
        <span className="relative inline-flex items-center font-semibold">
            {value?.toLocaleString() ?? '—'}
            <Tooltip title={"User converted via this action"}>
                <InfoCircleOutlined className="absolute -right-3 -top-2 cursor-help text-[10px] text-gray-500" />
            </Tooltip>
        </span>
    );

    const renderLeadComparisonCell = (name: string, without: number, withValue: number, withoutLabel: string, withLabel: string) => (
        <div className="grid h-full">
            {/* <div className="flex items-center justify-center border-r border-black font-semibold px-2 p-[1rem]">{name}</div> */}
            <div className="flex items-center justify-center gap-2 border-l border-black font-semibold px-2">
                {/* <span className="text-xs text-gray-600">{renderLeadCount(without, withoutLabel)}</span> */}
                {/* <span>|</span> */}
                <span className="text-xs justify-center test-center items-center text-gray-600">{renderLeadCount(withValue, withLabel)}</span>
            </div>
        </div>
    );

    const findNudge = (ad: Ad, name: string) =>
        (ad.nudges || []).flatMap((group) => group.values || []).find((nudge) => nudge.nudgeName === name);

    const findRetargeting = (ad: Ad, name: string) =>
        (ad.retargeting || ad.retargetting || []).find((item) => item.retargetingName === name);

    // -------- DATASOURCE --------

    const dataSource = rows.map((row) => {
        const record: any = {
            key: row.key,
            label: row.label,
            type: row.type,
        };

        ads.forEach((ad) => {
            const sorted = [...(ad.milestones || [])].sort(
                (a, b) => a.milestoneNum - b.milestoneNum
            );

            if (row.type === "ad") {
                record[ad.adId] = renderCell(ad.adName);
            }

            if (row.type === "preview") {
                record[ad.adId] = renderPreviewCell(ad);
            }

            if (row.type === "metric") {
                record[ad.adId] = renderMetricCell((ad as any)[row.key]);
            }

            if (row.type === "milestone") {
                record[ad.adId] = renderMilestoneCell(sorted[row.idx]);
            }

            if (row.type === "nudge") {
                const nudge = findNudge(ad, row.name);
                record[ad.adId] = nudge
                    ? renderLeadComparisonCell(nudge.nudgeName, nudge.leadWithoutNudge, nudge.leadWithNudge, "Without nudge", "With nudge")
                    : <div className="text-center">—</div>;
            }

            if (row.type === "retargeting") {
                const item = findRetargeting(ad, row.name);
                record[ad.adId] = item
                    ? renderLeadComparisonCell(item.retargetingName, item.leadWithoutRetargeting, item.leadWithRetargeting, "Without retargeting", "With retargeting")
                    : <div className="text-center">—</div>;
            }
        });

        return record;
    });

    // -------- COLUMN BASE STYLE --------

    const getCellStyle = (record: any) => ({
        padding: 0,
        border: "1px solid black",
        background:
            record.type === "metric"
                ? "#4f79a8"
                : record.type === "preview"
                    ? "#f8f9fa"
                    : record.type === "milestone"
                        ? "#eef2f7"
                        : "#fff",
    });

    // // -------- DATASOURCE --------
    // const dataSource = rows.map((row) => {
    //     const record: any = {
    //         key: row.key,
    //         label: row.label,
    //         type: row.type,
    //     };

    //     ads.forEach((ad) => {
    //         const sorted = [...(ad.milestones || [])].sort(
    //             (a, b) => a.milestoneNum - b.milestoneNum
    //         );

    //         if (row.type === "campaign") {
    //             record[ad.adId] = campaignName;
    //         } else if (row.type === "metric") {
    //             record[ad.adId] = (ad as any)[row.key]?.toLocaleString();
    //         } else if (row.type === "milestone") {
    //             const m = sorted[row.idx];

    //             record[ad.adId] = m ? (
    //                 <div
    //                     style={{
    //                         display: "flex",
    //                         justifyContent: "space-between",
    //                         alignItems: "center",
    //                         width: "100%",
    //                         fontSize: 13,
    //                     }}
    //                 >
    //                     {/* LEFT */}
    //                     <span style={{ fontWeight: 600 }}>
    //                         {m.milestoneName}
    //                     </span>

    //                     {/* RIGHT */}
    //                     <span style={{ fontVariantNumeric: "tabular-nums" }}>
    //                         {m.totalCount} |{" "}
    //                         <span style={{ fontWeight: 600 }}>{m.uniqueCount + "(U)"}</span>
    //                     </span>
    //                 </div>
    //             ) : (
    //                 "—"
    //             );
    //         }
    //     });

    //     return record;
    // });

    // -------- COLUMNS --------
    const columns: any = [
        {
            // title: "Ad Name",
            dataIndex: "label",
            key: "label",
            fixed: "left",
            width: 220,
            className: "p-0!",
            // border: "2px solid black",
            render: (text: string, record: any) => (
                <span
                    style={{
                        fontWeight: 600,
                        color: record.type === "metric" ? "#fff" : record.type === "ad" ? "#b55050ff" : "#000",
                    }}
                >
                    {text}
                </span>
            ),
            onCell: (record: any) => ({
                style: {
                    background: record.type === "metric" ? "#4f79a8" : record.type === "preview" ? "#f0f2f5" : "#d9d9d9",
                    border: "1px solid black",
                }
            }),
        },

        ...ads.map((ad) => ({
            // title: ad.adName,
            dataIndex: ad.adId,
            key: ad.adId,
            align: "center" as const,
            width: 260,
            render: (val: any, record: any) => val,
            className: "p-0!",
            onCell: (record: any) => ({
                style: {
                    background:
                        record.type === "metric"
                            ? "#e6dcb8"
                            : record.type === "preview"
                                ? "#f8f9fa"
                                : record.type === "milestone" || record.type === "nudge" || record.type === "retargeting"
                                    ? getMilestoneColor(record.key)
                                    : "#e6dcb8",
                    border: "1px solid black",
                    padding:
                        record.type === "milestone" || record.type === "nudge" || record.type === "retargeting" ? "0" : record.type === "preview" ? "4px" : ""
                },
                className: record.type === "milestone" || record.type === "nudge" || record.type === "retargeting" ? "no-padding-cell p-0!" : "",
            }),
        })),
    ];

    function getMilestoneColor(key: string) {
        const idx = parseInt(key.split("_")[1] || "0");
        const colors = ["#cfd8e3"];
        return colors[idx % colors.length];
    }

    return (
        <div>
            <Table
                columns={columns}
                dataSource={dataSource}
                showHeader={false}
                title={() => <div className="flex justify-between text-lg bg-[#1F344C] text-white p-0! rounded-t-lg p-0"><div className="text-center text-lg bg-[#1F344C] text-white p-2 rounded-t-lg p-0">Campaign Name :- {campaignName}</div><div className="text-center text-lg bg-[#1F344C] text-white m-2 rounded-t-lg p-0">Date Range :- {dateRange}</div></div>}
                pagination={false}
                bordered={false}
                scroll={{ x: 'max-content' }}
                className="custom-table"
            />

            <Modal
                open={!!previewAd}
                onCancel={() => setPreviewAd(null)}
                footer={null}
                title={previewAd?.adName ?? 'Ad Preview'}
                width={340}
                centered
                destroyOnClose
            >
                {previewAd?.adPreview && (
                    <div
                        className="ad-modal-iframe-wrapper"
                        dangerouslySetInnerHTML={{ __html: previewAd.adPreview }}
                    />
                )}
            </Modal>

        </div>
    );
}
